import * as babylon from "@babylonjs/core";

/** Width multiplier at the base (fat end) of each vector triangle. */
export const VECTOR_FIELD_BASE_WIDTH = 8;

export interface GradientOptions {
  colorLow: babylon.Color3;
  colorHigh: babylon.Color3;
  minMagnitude: number;
  maxMagnitude: number;
}

/**
 * Returns the gradient color for a given magnitude, interpolated between
 * colorLow and colorHigh based on the magnitude's position in the range.
 */
export function interpolateGradientColor(
  gradient: GradientOptions,
  magnitude: number,
): babylon.Color3 {
  const range = gradient.maxMagnitude - gradient.minMagnitude;
  const t = range > 0 ? (magnitude - gradient.minMagnitude) / range : 0.5;
  const clamped = Math.max(0, Math.min(1, t));
  return babylon.Color3.Lerp(gradient.colorLow, gradient.colorHigh, clamped);
}

/**
 * Computes the segment geometry, widths, colors, and default color for
 * a vector field visualization without creating the mesh. This is the
 * pure-computation core of createVectorFieldMesh.
 */
export function computeVectorFieldSegments(
  positions: babylon.Vector3[],
  vectors: babylon.Vector3[],
  stride: number,
  color: string,
  gradient?: GradientOptions,
): {
  segments: babylon.Vector3[][];
  widths: number[];
  colors: babylon.Color3[];
  defaultColor: babylon.Color3;
} {
  const safeStride = Math.max(1, Math.trunc(stride));
  const segments: babylon.Vector3[][] = [];
  const widths: number[] = [];
  const colors: babylon.Color3[] = [];
  const defaultColor = /^#[0-9a-f]{6}$/i.test(color)
    ? babylon.Color3.FromHexString(color)
    : babylon.Color3.White();

  for (let i = 0; i < positions.length && i < vectors.length; i += safeStride) {
    const start = positions[i];
    const end = start.add(vectors[i]);
    segments.push([start, end]);
    // Each segment has 2 points, each point gets a below/above width pair.
    // Base (start): fat, Tip (end): pointed.
    widths.push(VECTOR_FIELD_BASE_WIDTH, VECTOR_FIELD_BASE_WIDTH, 0, 0);

    if (gradient) {
      const segmentColor = interpolateGradientColor(
        gradient,
        vectors[i].length(),
      );
      // One color per point in the segment.
      colors.push(segmentColor, segmentColor);
    }
  }

  return { segments, widths, colors, defaultColor };
}

/**
 * Creates a single greased line mesh containing disconnected line segments,
 * one per sampled point, showing the direction and magnitude of a vector
 * at each position along a path. Each segment is rendered as a triangle
 * with a fat base tapering to a point at the tip.
 *
 * @param lineWidth - Base width multiplier passed to the greased line material.
 *   The effective rendered width at each point is `lineWidth * VECTOR_FIELD_BASE_WIDTH`.
 */
export function createVectorFieldMesh(
  name: string,
  color: string,
  lineWidth: number,
  positions: babylon.Vector3[],
  vectors: babylon.Vector3[],
  stride: number,
  scene: babylon.Scene,
  gradient?: GradientOptions,
): babylon.GreasedLineMesh {
  const { segments, widths, colors, defaultColor } = computeVectorFieldSegments(
    positions,
    vectors,
    stride,
    color,
    gradient,
  );

  return babylon.CreateGreasedLine(
    name,
    {
      points: segments,
      widths,
      widthDistribution:
        babylon.GreasedLineMeshWidthDistribution.WIDTH_DISTRIBUTION_NONE,
    },
    {
      color: defaultColor,
      width: lineWidth,
      sizeAttenuation: true,
      ...(gradient ? { useColors: true, colors } : {}),
    },
    scene,
  ) as babylon.GreasedLineMesh;
}
